import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { LearnWorkbenchShell } from "../components/layout/LearnWorkbenchShell";
import { ChordHorizontalDock } from "../components/chords/ChordHorizontalDock";
import { ChordPianoWorkbench } from "../components/chords/ChordPianoWorkbench";
import { ChordGuitarWorkbench } from "../components/chords/ChordGuitarWorkbench";
import { ChordImmersiveCanvas } from "../components/chords/ChordImmersiveCanvas";
import { useExploreSandbox } from "../hooks/useExploreSandbox";
import { ChordPracticeInspectorPanel } from "../components/chords/ChordPracticeInspectorPanel";
import { ChordTheoryInspector } from "../components/chords/ChordTheoryInspector";
import { buildChordToneMidis, inversionOptionsForChord } from "../lib/chordStaffEvents";
import { type NoteName, noteToSemitones, semitonesToNote, shouldUseFlats, midiToNoteName } from "../lib/guitarEngine";
import {
  type ExtendedChordQuality,
  type VoicingMode,
  type ChordVoicingResult,
  type ExplorerFilter,
  CHORD_CATALOG,
  getQualityCategories,
  getQualitiesByCategory,
  getChordDef,
  getClassicVoicings,
  generateExplorerVoicings,
} from "../lib/chordEngine";
import {
  getUkuleleClassicVoicings,
  generateUkuleleExplorerVoicings,
  type UkuleleChordQuality,
  type UkuleleExplorerFilter,
  type UkuleleVoicingResult,
} from "../lib/ukuleleChordEngine";
import { getInstrumentConfig } from "../lib/instrumentEngine";
import { useInstrument } from "../contexts/InstrumentContext";
import { useSpaciousWorkbench } from "../lib/useSpaciousWorkbench";
import { useAudioEngine } from "../hooks/useAudioEngine";
import { useGuitarSynth } from "../hooks/useGuitarSynth";
import { useTeacherId } from "../hooks/useTeacherId";
import { useTeacherClassrooms } from "../hooks/useTeacherClassrooms";
import { useStudentFavorites } from "../hooks/useStudentFavorites";
import { TaskGeneratorModal } from "../components/TaskGeneratorModal";
import { HelpTooltip } from "../components/ui/HelpTooltip";
import { Bookmark } from "lucide-react";
import { EmptyState } from "../components/ui/EmptyState";
import { serializeChordState, type TaskType } from "../lib/taskEngine";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ProgressionSequencer } from "../components/progressions/ProgressionSequencer";
import { ProgressionPlayer } from "../components/progressions/ProgressionPlayer";
import { TransposeControl } from "../components/shared/TransposeControl";
import { transposeProgression, parseChordName, chordToRomanNumeral, getQualityDisplayString } from "../lib/musicTheory";
import { useProgressionLibrary, type ProgressionTag, PROGRESSION_TAGS } from "../hooks/useProgressionLibrary";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const NOTES: NoteName[] = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

export const ChordBuilderPage: React.FC = () => {
  const { instrument, surface } = useInstrument();
  useSpaciousWorkbench();
  const stringCount = getInstrumentConfig(instrument).strings.length;
  const isUkulele = instrument === "ukulele";

  // Array dinámico de cuerdas [N, ..., 1]
  const ALL_STRINGS = useMemo(() => Array.from({ length: stringCount }, (_, i) => stringCount - i), [stringCount]);

  // --- Estado principal ---
  const [root, setRoot] = useState<NoteName>("C");
  const [quality, setQuality] = useState<ExtendedChordQuality>("major");
  const [mode, setMode] = useState<VoicingMode>("classic");
  const [selectedVoicingIndex, setSelectedVoicingIndex] = useState(0);
  const [stringOrder, setStringOrder] = useState<"tab" | "physical">("physical");
  const [leftHanded, setLeftHanded] = useState(false);
  const [activeTab, setActiveTab] = useState("acorde");
  const isSandbox = useExploreSandbox();
  const showImmersiveChord = isSandbox && activeTab === "acorde" && surface !== "keyboard";

  // --- Sub-módulo Progresiones ---
  const [progression, setProgression] = useState<string[]>(["C", "Am", "F", "G"]);
  const [progKey, setProgKey] = useState<NoteName>("C");
  const [progTranspose, setProgTranspose] = useState(0);
  const [progCapo, setProgCapo] = useState(0);
  const [progBpm, setProgBpm] = useState(80);
  const [progIsPlaying, setProgIsPlaying] = useState(false);
  const [progActiveIndex, setProgActiveIndex] = useState(0);
  const [neckFade, setNeckFade] = useState(true);
  const [progSaveOpen, setProgSaveOpen] = useState(false);
  const [progSaveName, setProgSaveName] = useState("");
  const [progSaveTags, setProgSaveTags] = useState<ProgressionTag[]>([]);

  const { items: savedProgressions, saveProgression, deleteProgression } = useProgressionLibrary();

  // Helper para obtener voicing desde nombre de acorde (para reutilizar GuitarNeck en progresiónes)
  const getVoicingFromChordName = useCallback((chordName: string): ChordVoicingResult | null => {
    const parsed = parseChordName(chordName);
    if (!parsed) return null;
    const voicings = getClassicVoicings(parsed.root, parsed.quality);
    return voicings[0] || null;
  }, []);

  // Helper: notas constitutivas de un acorde por nombre
  const getChordNotes = useCallback((chordName: string): string[] => {
    const parsed = parseChordName(chordName);
    if (!parsed) return [];
    const def = getChordDef(parsed.quality);
    const rootSemi = noteToSemitones(parsed.root);
    const useFlats = shouldUseFlats(parsed.root, def?.quality.includes("m") && !def.quality.includes("maj") ? "minor" : "major");
    const intervals = def?.intervals ?? [0, 4, 7];
    return intervals.map((int) => semitonesToNote((rootSemi + int) % 12, useFlats));
  }, []);

  const progVoicing = useMemo(() => {
    const activeChord = progression[progActiveIndex];
    return activeChord ? getVoicingFromChordName(activeChord) : null;
  }, [progression, progActiveIndex, getVoicingFromChordName]);

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [activeChordCategory, setActiveChordCategory] = useState("triada");
  const [showNotePicker, setShowNotePicker] = useState(false);
  const [showQualityPicker, setShowQualityPicker] = useState(false);

  // --- Práctica polifónica con Basic Pitch ---
  const { playFret, playChord, playNote } = useGuitarSynth();
  const {
    state: engineState,
    init,
    detectChord,
  } = useAudioEngine();
  const [isRecording, setIsRecording] = useState(false);
  const [lastResult, setLastResult] = useState<{
    matched: number[];
    missing: number[];
    extra: number[];
    score: number;
  } | null>(null);

  // --- Modo arpegio ---
  const [detectionMode, setDetectionMode] = useState<"chord" | "arpeggio">("chord");
  const [arpeggioIndex, setArpeggioIndex] = useState(0);
  const [arpeggioResults, setArpeggioResults] = useState<boolean[]>([]);
  const [arpeggioPhase, setArpeggioPhase] = useState<"notes" | "chord" | "done">("notes");
  const [isArpeggioRecording, setIsArpeggioRecording] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);

  const { teacherId, teacherName } = useTeacherId();
  const { classrooms, members } = useTeacherClassrooms(teacherId, teacherName);
  const { toggleFavorite, isFavorite } = useStudentFavorites();

  // --- Muteo manual (override del voicing actual) ---
  const [userMutedStrings, setUserMutedStrings] = useState<number[]>([]);

  // --- Filtros Explorador ---
  const [filter, setFilter] = useState<ExplorerFilter>({
    selectedStrings: [4, 3, 2, 1],
    allowOpenStrings: true,
    allowBarre: true,
    maxFret: 15,
    maxSpan: 4,
    inversion: "fundamental",
    voicingType: "all",
  });

  // --- Calcular voicings ---
  const voicings = useMemo(() => {
    if (mode === "classic") {
      return getClassicVoicings(root, quality);
    }
    return generateExplorerVoicings(root, quality, filter);
  }, [root, quality, mode, filter]);

  const currentVoicing = voicings[selectedVoicingIndex] || null;

  // Combinar muted strings del voicing + muteo manual del usuario
  const effectiveMutedStrings = useMemo(() => {
    if (!currentVoicing) return [];
    const base = new Set(currentVoicing.mutedStrings);
    userMutedStrings.forEach((s) => base.add(s));
    return Array.from(base).sort((a, b) => b - a);
  }, [currentVoicing, userMutedStrings]);

  // Resetear navegación y muteo cuando cambian parámetros importantes
  React.useEffect(() => {
    setSelectedVoicingIndex(0);
    setUserMutedStrings([]);
  }, [root, quality, mode, filter]);

  const handlePrev = useCallback(() => {
    setSelectedVoicingIndex((i) => (i > 0 ? i - 1 : voicings.length - 1));
    setUserMutedStrings([]);
  }, [voicings.length]);

  const handleNext = useCallback(() => {
    setSelectedVoicingIndex((i) => (i < voicings.length - 1 ? i + 1 : 0));
    setUserMutedStrings([]);
  }, [voicings.length]);

  const toggleMuteString = useCallback((s: number) => {
    setUserMutedStrings((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  }, []);

  // --- Navegación circular de notas ---
  const handlePrevNote = useCallback(() => {
    const idx = NOTES.indexOf(root);
    const prev = idx > 0 ? NOTES[idx - 1] : NOTES[NOTES.length - 1];
    setRoot(prev);
  }, [root]);

  const handleNextNote = useCallback(() => {
    const idx = NOTES.indexOf(root);
    const next = idx < NOTES.length - 1 ? NOTES[idx + 1] : NOTES[0];
    setRoot(next);
  }, [root]);

  // --- Navegación circular de calidades ---
  const qualitiesInCategory = getQualitiesByCategory(activeChordCategory);
  const handlePrevQuality = useCallback(() => {
    const idx = qualitiesInCategory.indexOf(quality);
    const prev = idx > 0 ? qualitiesInCategory[idx - 1] : qualitiesInCategory[qualitiesInCategory.length - 1];
    setQuality(prev);
  }, [quality, qualitiesInCategory]);

  const handleNextQuality = useCallback(() => {
    const idx = qualitiesInCategory.indexOf(quality);
    const next = idx < qualitiesInCategory.length - 1 ? qualitiesInCategory[idx + 1] : qualitiesInCategory[0];
    setQuality(next);
  }, [quality, qualitiesInCategory]);

  const def = getChordDef(quality);

  const inversionOptions = useMemo(
    () => inversionOptionsForChord(def?.intervals.length ?? 3),
    [def]
  );

  useEffect(() => {
    setFilter((f) => {
      if (inversionOptions.includes(f.inversion)) return f;
      return { ...f, inversion: inversionOptions[0] ?? "fundamental" };
    });
  }, [inversionOptions]);

  const handlePrevInversion = useCallback(() => {
    setFilter((f) => {
      const idx = inversionOptions.indexOf(f.inversion);
      const prev =
        idx > 0 ? inversionOptions[idx - 1] : inversionOptions[inversionOptions.length - 1];
      return { ...f, inversion: prev };
    });
  }, [inversionOptions]);

  const handleNextInversion = useCallback(() => {
    setFilter((f) => {
      const idx = inversionOptions.indexOf(f.inversion);
      const next =
        idx < inversionOptions.length - 1 ? inversionOptions[idx + 1] : inversionOptions[0];
      return { ...f, inversion: next };
    });
  }, [inversionOptions]);

  const voicingTypeCycle: ExplorerFilter["voicingType"][] = ["all", "spread", "drop2", "drop3"];
  const handleCycleVoicingType = useCallback(() => {
    setFilter((f) => {
      const idx = voicingTypeCycle.indexOf(f.voicingType);
      const next = voicingTypeCycle[(idx + 1) % voicingTypeCycle.length];
      return { ...f, voicingType: next };
    });
  }, []);

  const handlePlayChordPiano = useCallback(() => {
    const tones = buildChordToneMidis(root, quality, filter.inversion, filter.voicingType);
    tones.forEach((t, i) => {
      const octave = Math.floor(t.midi / 12) - 1;
      setTimeout(() => playNote(t.step, octave, 1200), i * 35);
    });
  }, [root, quality, filter.inversion, filter.voicingType, playNote]);

  // Notas objetivo del acorde TEÓRICO (no del voicing específico)
  // Esto asegura que C Mayor siempre evalúe C+E+G sin importar qué voicing se muestre
  const targetNotes = useMemo(() => {
    if (!def) return new Set<number>();
    const rootSemi = noteToSemitones(root);
    return new Set(def.intervals.map((int) => (rootSemi + int) % 12));
  }, [def, root]);

  const targetNoteNames = useMemo(() => {
    if (!def) return [];
    const rootSemi = noteToSemitones(root);
    return def.intervals.map((int) => semitonesToNote(rootSemi + int));
  }, [def, root]);

  const chordTones = useMemo(
    () => buildChordToneMidis(root, quality, filter.inversion, filter.voicingType),
    [root, quality, filter.inversion, filter.voicingType]
  );

  // Precargar modelo automáticamente al montar la página
  useEffect(() => {
    if (!engineState.isReady && !engineState.isLoading) {
      init();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Resetear resultado cuando cambia el acorde
  useEffect(() => {
    setLastResult(null);
    setArpeggioIndex(0);
    setArpeggioResults([]);
    setArpeggioPhase("notes");
  }, [root, quality, selectedVoicingIndex, effectiveMutedStrings]);

  // Crossfade del mástil al cambiar de acorde en progresiónes
  useEffect(() => {
    if (activeTab !== "progresiónes") return;
    setNeckFade(false);
    const t = setTimeout(() => setNeckFade(true), 180);
    return () => clearTimeout(t);
  }, [progActiveIndex, activeTab]);

  const handlePlayChord = useCallback(() => {
    if (!currentVoicing) return;
    const notes = currentVoicing.positions
      .filter((p) => !effectiveMutedStrings.includes(p.string))
      .map((p) => ({ string: p.string, fret: p.fret }));
    playChord(notes, 1200, 30);
  }, [currentVoicing, effectiveMutedStrings, playChord]);

  // --- Detección polifónica con Basic Pitch ---
  const getPitchClassSet = (notes: number[]) => new Set(notes.map((n) => n % 12));

  const handleListenChord = useCallback(async () => {
    if (!engineState.isReady) {
      await init();
    }
    setIsRecording(true);
    setLastResult(null);
    const chord = await detectChord(2500);
    setIsRecording(false);

    const detectedPitches = chord ? chord.notes.map((n) => Math.round(n.pitch)) : [];
    const detectedPC = getPitchClassSet(detectedPitches);
    const expectedPC = getPitchClassSet(Array.from(targetNotes));

    const matched = Array.from(expectedPC).filter((pc) => detectedPC.has(pc));
    const missing = Array.from(expectedPC).filter((pc) => !detectedPC.has(pc));
    const extra = Array.from(detectedPC).filter((pc) => !expectedPC.has(pc));

    const score = expectedPC.size > 0 ? matched.length / expectedPC.size : 0;
    setLastResult({ matched, missing, extra, score });
  }, [engineState.isReady, init, detectChord, targetNotes]);

  // --- Modo arpegio ---
  const handleArpeggioNote = useCallback(async () => {
    if (!engineState.isReady) {
      await init();
    }
    setIsArpeggioRecording(true);
    const chord = await detectChord(1500);
    setIsArpeggioRecording(false);

    const detectedPCs = new Set(chord?.pitchClasses || []);
    const targetNote = targetNoteNames[arpeggioIndex];
    const targetPC = targetNote ? noteToSemitones(targetNote) % 12 : -1;

    const hit = targetPC >= 0 && detectedPCs.has(targetPC);
    const newResults = [...arpeggioResults, hit];
    setArpeggioResults(newResults);

    if (hit) {
      const next = arpeggioIndex + 1;
      if (next >= targetNoteNames.length) {
        setArpeggioPhase("chord");
      } else {
        setArpeggioIndex(next);
      }
    }
    // Si no acertó, no avanza � repite la misma nota
  }, [engineState.isReady, init, detectChord, targetNoteNames, arpeggioIndex, arpeggioResults]);

  const handleArpeggioChord = useCallback(async () => {
    if (!engineState.isReady) {
      await init();
    }
    setIsArpeggioRecording(true);
    const chord = await detectChord(2500);
    setIsArpeggioRecording(false);

    const detectedPitches = chord ? chord.notes.map((n) => Math.round(n.pitch)) : [];
    const detectedPC = getPitchClassSet(detectedPitches);
    const expectedPC = getPitchClassSet(Array.from(targetNotes));

    const matched = Array.from(expectedPC).filter((pc) => detectedPC.has(pc));
    const missing = Array.from(expectedPC).filter((pc) => !detectedPC.has(pc));
    const extra = Array.from(detectedPC).filter((pc) => !expectedPC.has(pc));

    const chordScore = expectedPC.size > 0 ? matched.length / expectedPC.size : 0;
    const arpeggioScore = arpeggioResults.length > 0
      ? arpeggioResults.filter(Boolean).length / arpeggioResults.length
      : 0;
    const combinedScore = (arpeggioScore + chordScore) / 2;

    setLastResult({ matched, missing, extra, score: combinedScore });
    setArpeggioPhase("done");
  }, [engineState.isReady, init, detectChord, targetNotes, targetNoteNames, arpeggioResults]);

  // ������ Selector de calidad compacto ������
  const categories = getQualityCategories();
  const selectedCategory = categories.find((c) =>
    getQualitiesByCategory(c.id).includes(quality)
  ) || categories[0];

  // ������ Sidebar ������
  const sidebar = (
    <div className="space-y-3">
      {activeTab === "progresiónes" ? (
        <div className="space-y-4">
          {/* === MODO PROGRESIONES === */}
          <div className="text-center pb-3 border-b border-white/10">
            <div className="text-lg font-serif font-bold italic text-slate-100">Progresiones</div>
            <div className="text-[11px] text-slate-400 mt-0.5">
              {progKey} Mayor · {progression.filter(Boolean).length} acordes
            </div>
          </div>

          {/* Buscador de acordes */}
          <div className="text-center space-y-2">
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={handlePrevNote}
                className="w-8 h-8 rounded-full bg-white/[0.07] flex items-center justify-center text-slate-300 hover:bg-white/15 transition-all"
                title="Nota anterior"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
              </button>
              <button
                onClick={() => setShowNotePicker(!showNotePicker)}
                className="w-14 h-14 rounded-full bg-gradient-to-br from-[var(--bordona-accent)] to-[var(--bordona-accent-hover)] text-white flex items-center justify-center text-2xl font-serif font-bold shadow-lg hover:scale-105 transition-transform"
                title="Cambiar nota raíz"
              >
                {root}
              </button>
              <button
                onClick={handleNextNote}
                className="w-8 h-8 rounded-full bg-white/[0.07] flex items-center justify-center text-slate-300 hover:bg-white/15 transition-all"
                title="Nota siguiente"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>
              </button>
            </div>
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={handlePrevQuality}
                className="w-7 h-7 rounded-full bg-white/[0.07] flex items-center justify-center text-slate-300 hover:bg-white/15 transition-all"
                title="Calidad anterior"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
              </button>
              <button
                onClick={() => setShowQualityPicker(!showQualityPicker)}
                className="text-base font-serif font-bold italic text-slate-100 hover:text-[var(--bordona-accent)] transition-colors px-2"
                title="Cambiar calidad"
              >
                {def?.label || quality}
              </button>
              <button
                onClick={handleNextQuality}
                className="w-7 h-7 rounded-full bg-white/[0.07] flex items-center justify-center text-slate-300 hover:bg-white/15 transition-all"
                title="Calidad siguiente"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>
              </button>
            </div>
            <div className="text-[11px] font-sans text-slate-400">
              {mode === "classic" ? "Clásico" : "Explorador"} · {def?.degreeNames.join(" · ")}
            </div>
          </div>

          {/* Pickers */}
          {showNotePicker && (
            <div className="p-2 rounded-xl bg-white/[0.08] border border-white/10 shadow-none">
              <div className="grid grid-cols-6 gap-1">
                {NOTES.map((n) => (
                  <button
                    key={n}
                    onClick={() => { setRoot(n); setShowNotePicker(false); }}
                    className={`h-8 rounded-md text-sm font-sans font-bold transition-all ${root === n ? "bg-[var(--bordona-accent)] text-white shadow-sm" : "bg-white/[0.07] text-slate-300 hover:bg-white/10"}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          )}
          {showQualityPicker && (
            <div className="p-2 rounded-xl bg-white/[0.08] border border-white/10 shadow-none">
              <div className="space-y-2">
                {categories.map((cat) => (
                  <div key={cat.id}>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-1">{cat.label}</div>
                    <div className="flex flex-wrap gap-1">
                      {getQualitiesByCategory(cat.id).map((q) => {
                        const qDef = getChordDef(q);
                        return (
                          <button
                            key={q}
                            onClick={() => { setQuality(q); setActiveChordCategory(cat.id); setShowQualityPicker(false); }}
                            className={`px-2 py-1 rounded-md text-[10px] font-sans transition-all ${quality === q ? "bg-[var(--bordona-accent)] text-white" : "bg-white/[0.07] text-slate-300 hover:bg-white/10"}`}
                          >
                            {qDef?.label || q}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Modalidad */}
          <div className="flex gap-1">
            {[
              { id: "classic" as VoicingMode, label: "Clásico", color: "#F59E0B" },
              { id: "explorer" as VoicingMode, label: "Explorador", color: "#5BA8A8" },
            ].map((m) => (
              <button
                key={m.id}
                onClick={() => setMode(m.id)}
                className={`flex-1 py-1.5 rounded-lg text-xs font-sans font-medium transition-all ${mode === m.id ? "text-white shadow-sm" : "bg-white/[0.07] text-slate-300 hover:bg-white/10"}`}
                style={mode === m.id ? { backgroundColor: m.color } : {}}
              >
                {m.label}
              </button>
            ))}
          </div>

          {/* Selector de tipo */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-1.5">Tipo</div>
            <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-hide">
              {categories.map((cat) => {
                const isActive = cat.id === activeChordCategory;
                return (
                  <button
                    key={cat.id}
                    onClick={() => { setActiveChordCategory(cat.id); const first = getQualitiesByCategory(cat.id)[0]; if (first) setQuality(first); }}
                    className={`px-2.5 py-1 rounded-lg text-[10px] font-sans font-medium whitespace-nowrap transition-all ${isActive ? "bg-slate-800 text-white" : "bg-white/[0.07] text-slate-400 hover:bg-white/10 border border-white/10"}`}
                  >
                    {cat.label}
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-3 gap-1 mt-1.5">
              {getQualitiesByCategory(activeChordCategory).map((q) => {
                const qDef = getChordDef(q);
                return (
                  <button
                    key={q}
                    onClick={() => setQuality(q)}
                    className={`px-1 py-1.5 rounded-lg text-[11px] font-sans transition-all ${quality === q ? "bg-[var(--bordona-accent)] text-white shadow-sm" : "bg-white/[0.07] text-slate-200 hover:bg-white/10 border border-white/10"}`}
                  >
                    {qDef?.label || q}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Info compacta */}
          {def && (
            <div
              className="p-2.5 rounded-xl bg-white/[0.08] border border-white/10"
              style={{ boxShadow: "0 4px 20px rgba(45,42,38,0.04)" }}
            >
              <div className="text-xs font-sans font-semibold text-slate-100">{root} {def.label}</div>
              <div className="text-[10px] text-slate-400 font-sans mt-0.5">{def.degreeNames.join(" · ")}</div>
            </div>
          )}

          {/* Añadir a progresión */}
          <button
            onClick={() => {
              const chordName = `${root}${getQualityDisplayString(quality)}`;
              const firstEmpty = progression.findIndex((c) => !c);
              if (firstEmpty >= 0) {
                const next = [...progression];
                next[firstEmpty] = chordName;
                setProgression(next);
                setProgActiveIndex(firstEmpty);
              } else {
                setProgression([...progression, chordName]);
                setProgActiveIndex(progression.length);
              }
            }}
            className="w-full py-2 rounded-lg text-xs font-sans font-medium bg-[var(--bordona-accent)] text-white hover:bg-[var(--bordona-accent-hover)] transition-all"
          >
            �~" Añadir a progresión
          </button>

          {/* Presets */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-1.5">Presets</div>
            <div className="grid grid-cols-2 gap-1">
              {[
                { name: "Pop", chords: ["C", "G", "Am", "F"] },
                { name: "Jazz", chords: ["Dm7", "G7", "Cmaj7", "Cmaj7"] },
                { name: "Rock", chords: ["E", "A", "B", "E"] },
                { name: "Blues", chords: ["A7", "D7", "E7", "A7"] },
              ].map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => setProgression(preset.chords)}
                  className="px-2 py-1.5 rounded-lg text-[10px] font-sans bg-white/[0.08] border border-white/10 text-slate-300 hover:bg-white/10 transition-all"
                >
                  {preset.name}
                </button>
              ))}
            </div>
          </div>

          {/* Guardadas */}
          {savedProgressions.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-1.5">Guardadas</div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {savedProgressions.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setProgression(item.chords);
                      setProgKey(item.keySignature as NoteName);
                    }}
                    className="w-full text-left px-2 py-1.5 rounded-lg text-[11px] font-sans bg-white/[0.08] border border-white/10 text-slate-100 hover:bg-white/10 transition-all"
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Guardar */}
          <button
            onClick={() => setProgSaveOpen(true)}
            className="w-full py-2 rounded-lg text-xs font-sans font-medium bg-[var(--bordona-accent)] text-white hover:bg-[var(--bordona-accent-hover)] transition-all"
          >
            Guardar progresión
          </button>
        </div>
      ) : (
        <>
          {/* Header: Selector circular de nota + calidad */}
      <div className="text-center pb-3 border-b border-white/10 space-y-3">
        {/* Selector de nota */}
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={handlePrevNote}
            className="w-8 h-8 rounded-full bg-white/[0.07] flex items-center justify-center text-slate-300 hover:bg-white/15 transition-all"
            title="Nota anterior"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
          </button>
          <button
            onClick={() => setShowNotePicker(!showNotePicker)}
            className="w-16 h-16 rounded-full bg-gradient-to-br from-[var(--bordona-accent)] to-[var(--bordona-accent-hover)] text-white flex items-center justify-center text-3xl font-serif font-bold shadow-lg hover:scale-105 transition-transform"
            title="Cambiar nota raíz"
          >
            {root}
          </button>
          <button
            onClick={handleNextNote}
            className="w-8 h-8 rounded-full bg-white/[0.07] flex items-center justify-center text-slate-300 hover:bg-white/15 transition-all"
            title="Nota siguiente"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>
          </button>
        </div>

        {/* Selector de calidad */}
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={handlePrevQuality}
            className="w-7 h-7 rounded-full bg-white/[0.07] flex items-center justify-center text-slate-300 hover:bg-white/15 transition-all"
            title="Calidad anterior"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
          </button>
          <button
            onClick={() => setShowQualityPicker(!showQualityPicker)}
            className="text-lg font-serif font-bold italic text-slate-100 hover:text-[var(--bordona-accent)] transition-colors px-2"
            title="Cambiar calidad"
          >
            {def?.label || quality}
          </button>
          <button
            onClick={handleNextQuality}
            className="w-7 h-7 rounded-full bg-white/[0.07] flex items-center justify-center text-slate-300 hover:bg-white/15 transition-all"
            title="Calidad siguiente"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>
          </button>
        </div>

        <div className="text-[11px] font-sans text-slate-400">
          {mode === "classic" ? "Clásico" : "Explorador"} · {def?.degreeNames.join(" · ")}
        </div>

        <button
          onClick={() => {
            toggleFavorite({
              type: "chord",
              title: `${root} ${def?.label || quality}`,
              subtitle: mode === "classic" ? "Clásico" : "Explorador",
              url: "/chords/explorer",
            });
          }}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-sans font-medium transition-all ${
            isFavorite("/chords/explorer")
              ? "bg-[var(--bordona-accent-soft)] text-[var(--bordona-accent)] border border-[var(--bordona-accent-border)]"
              : "bg-white/[0.07] text-slate-400 border border-white/10 hover:bg-white/10 hover:text-slate-300"
          }`}
        >
          <Bookmark className={`w-3 h-3 ${isFavorite("/chords/explorer") ? "fill-current" : ""}`} />
          {isFavorite("/chords/explorer") ? "Guardado" : "Guardar acorde"}
        </button>

        {/* Picker de notas */}
        {showNotePicker && (
          <div className="p-2 rounded-xl bg-white/[0.08] border border-white/10 shadow-none">
            <div className="grid grid-cols-6 gap-1">
              {NOTES.map((n) => (
                <button
                  key={n}
                  onClick={() => { setRoot(n); setShowNotePicker(false); }}
                  className={`h-8 rounded-md text-sm font-sans font-bold transition-all ${
                    root === n
                      ? "bg-[var(--bordona-accent)] text-white shadow-sm"
                      : "bg-white/[0.07] text-slate-300 hover:bg-white/10"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Picker de calidades */}
        {showQualityPicker && (
          <div className="p-2 rounded-xl bg-white/[0.08] border border-white/10 shadow-none">
            <div className="space-y-2">
              {categories.map((cat) => (
                <div key={cat.id}>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-1">{cat.label}</div>
                  <div className="flex flex-wrap gap-1">
                    {getQualitiesByCategory(cat.id).map((q) => {
                      const qDef = getChordDef(q);
                      return (
                        <button
                          key={q}
                          onClick={() => { setQuality(q); setActiveChordCategory(cat.id); setShowQualityPicker(false); }}
                          className={`px-2 py-1 rounded-md text-[10px] font-sans transition-all ${
                            quality === q
                              ? "bg-[var(--bordona-accent)] text-white"
                              : "bg-white/[0.07] text-slate-300 hover:bg-white/10"
                          }`}
                        >
                          {qDef?.label || q}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modalidad */}
      <div className="flex gap-1">
        {[
          { id: "classic" as VoicingMode, label: "Clásico", color: "#F59E0B" },
          { id: "explorer" as VoicingMode, label: "Explorador", color: "#5BA8A8" },
        ].map((m) => (
          <button
            key={m.id}
            onClick={() => setMode(m.id)}
            className={`flex-1 py-1.5 rounded-lg text-xs font-sans font-medium transition-all ${
              mode === m.id
                ? "text-white shadow-sm"
                : "bg-white/[0.07] text-slate-300 hover:bg-white/10"
            }`}
            style={mode === m.id ? { backgroundColor: m.color } : {}}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Selector de tipo (tabs) */}
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-1.5">
          Tipo
        </div>
        <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-hide">
          {categories.map((cat) => {
            const isActive = cat.id === activeChordCategory;
            return (
              <button
                key={cat.id}
                onClick={() => {
                  setActiveChordCategory(cat.id);
                  const first = getQualitiesByCategory(cat.id)[0];
                  if (first) setQuality(first);
                }}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-sans font-medium whitespace-nowrap transition-all ${
                  isActive
                    ? "bg-slate-800 text-white"
                    : "bg-white/[0.07] text-slate-400 hover:bg-white/10 border border-white/10"
                }`}
              >
                {cat.label}
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-3 gap-1 mt-1.5">
          {getQualitiesByCategory(activeChordCategory).map((q) => {
            const qDef = getChordDef(q);
            return (
              <button
                key={q}
                onClick={() => setQuality(q)}
                className={`px-1 py-1.5 rounded-lg text-[11px] font-sans transition-all ${
                  quality === q
                    ? "bg-[var(--bordona-accent)] text-white shadow-sm"
                    : "bg-white/[0.07] text-slate-200 hover:bg-white/10 border border-white/10"
                }`}
              >
                {qDef?.label || q}
              </button>
            );
          })}
        </div>
      </div>

      {/* Info compacta */}
      {def && (
        <div
          className="p-2.5 rounded-xl bg-white/[0.08] border border-white/10"
          style={{ boxShadow: "0 4px 20px rgba(45,42,38,0.04)" }}
        >
          <div className="text-xs font-sans font-semibold text-slate-100">
            {root} {def.label}
          </div>
          <div className="text-[10px] text-slate-400 font-sans mt-0.5">
            {def.degreeNames.join(" · ")}
          </div>
        </div>
      )}

        </>
      )}
    </div>
  );

  // ������ Inspector (solo info, sin navegación) ������
  const inspector = (
    <div className="space-y-4">
      {activeTab === "progresiónes" ? (
        <div className="space-y-3">
          {/* Acorde actual */}
          {progression[progActiveIndex] && (
            <div className="p-4 rounded-2xl bg-white" style={{ boxShadow: "0 4px 20px rgba(45,42,38,0.04)" }}>
              <div className="text-2xl font-serif font-bold italic text-slate-100 leading-tight">
                {progression[progActiveIndex]}
              </div>
              <div className="mt-1.5 text-sm font-sans text-slate-500">
                {getChordNotes(progression[progActiveIndex]).join(" · ")}
              </div>
              <div className="mt-1 text-[11px] font-sans text-slate-400">
                {chordToRomanNumeral(progression[progActiveIndex], progKey)} � {(() => {
                  const r = chordToRomanNumeral(progression[progActiveIndex], progKey);
                  if (r.startsWith("I") && !r.startsWith("II") && !r.startsWith("III") && !r.startsWith("IV") && !r.startsWith("V") && !r.startsWith("VI") && !r.startsWith("VII")) return "Tónica";
                  if (r.startsWith("vi")) return "Tónica";
                  if (r.startsWith("ii") || r.startsWith("IV")) return "Subdominante";
                  if (r.startsWith("V") || r.startsWith("vii")) return "Dominante";
                  return "Otro";
                })()}
              </div>
            </div>
          )}

          {/* Tonalidad */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-1.5">Tonalidad</div>
            <div className="flex items-center gap-2">
              <select
                value={progKey}
                onChange={(e) => setProgKey(e.target.value as NoteName)}
                className="flex-1 px-2 py-1.5 rounded-lg text-sm font-sans bg-white/[0.08] border border-white/10 text-slate-100"
              >
                {NOTES.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Transpose */}
          <TransposeControl
            transpose={progTranspose}
            capo={progCapo}
            onTransposeChange={(t) => {
              setProgTranspose(t);
              if (t !== 0) setProgression(transposeProgression(progression, t));
            }}
            onCapoChange={setProgCapo}
          />

          {/* Lista de secuencia */}
          <div className="p-3 rounded-2xl bg-white" style={{ boxShadow: "0 4px 20px rgba(45,42,38,0.04)" }}>
            <h4 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-2">Secuencia</h4>
            <div className="space-y-1">
              {progression.filter(Boolean).map((chord, i) => {
                const r = chordToRomanNumeral(chord, progKey);
                const isCurrent = i === progActiveIndex;
                return (
                  <div
                    key={i}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-xs font-sans cursor-pointer transition-colors ${
                      isCurrent ? "bg-[var(--bordona-accent-soft)] text-[var(--bordona-accent)] font-semibold" : "text-slate-300 hover:bg-white/[0.07]"
                    }`}
                    onClick={() => setProgActiveIndex(i)}
                  >
                    <span className="text-[10px] text-slate-400 w-4">{i + 1}</span>
                    <span className="flex-1">{chord}</span>
                    <span className="text-[10px] text-slate-400">{r}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <>
          {def && (
            <ChordTheoryInspector
              root={root}
              def={def}
              inversion={filter.inversion}
              voicingType={filter.voicingType}
              tones={chordTones}
              surface={surface}
            />
          )}

      {surface !== "keyboard" && currentVoicing && (
        <div
          className="p-4 rounded-2xl bg-white/[0.08] border border-white/10 space-y-1 text-[11px] text-slate-400 font-sans"
          style={{ boxShadow: "0 4px 20px rgba(45,42,38,0.04)" }}
        >
          {currentVoicing.shapeName && (
            <div className="text-slate-100 font-semibold">{currentVoicing.shapeName}</div>
          )}
          <div>
            {currentVoicing.stringCount} cuerdas · Span {currentVoicing.span} · Max T
            {currentVoicing.maxFret}
          </div>
          {currentVoicing.hasOpenStrings && (
            <div className="text-[#5A7A3A]">Cuerdas al aire</div>
          )}
          {currentVoicing.barre && (
            <div className="text-[var(--bordona-accent)]">
              Cejilla T{currentVoicing.barre.fret}
            </div>
          )}
          {effectiveMutedStrings.length > 0 && (
            <div className="text-slate-400">
              Cuerdas mutadas: {effectiveMutedStrings.sort((a, b) => b - a).join(", ")}
            </div>
          )}
        </div>
      )}

      {surface !== "keyboard" && currentVoicing && currentVoicing.positions.some((p) => p.finger !== undefined) && (
        <div
          className="p-4 rounded-2xl bg-white"
          style={{ boxShadow: "0 4px 20px rgba(45,42,38,0.04)" }}
        >
          <h4 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-2">
            Digitación
          </h4>
          <div className="space-y-1.5">
            {currentVoicing.positions
              .filter((p) => !effectiveMutedStrings.includes(p.string))
              .sort((a, b) => b.string - a.string)
              .map((pos, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm font-sans">
                  <span className="w-6 h-6 rounded-full bg-slate-800 text-white text-xs flex items-center justify-center font-bold">
                    {pos.finger || 0}
                  </span>
                  <span className="text-slate-100 font-medium">{pos.note}</span>
                  <span className="text-slate-400 text-xs">
                    C{pos.string} T{pos.fret}
                  </span>
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded ${
                      pos.degree === "R"
                        ? "bg-[var(--bordona-accent-soft)] text-[var(--bordona-accent)]"
                        : pos.degree === "5" || pos.degree === "b5" || pos.degree === "#5"
                        ? "bg-[#22C55E]/10 text-[#22C55E]"
                        : "bg-[#06B6D4]/10 text-[#06B6D4]"
                    }`}
                  >
                    {pos.degree}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}

      {def && (
        <ChordPracticeInspectorPanel
          targetNoteNames={targetNoteNames}
          detectionMode={detectionMode}
          setDetectionMode={setDetectionMode}
          arpeggioPhase={arpeggioPhase}
          setArpeggioPhase={setArpeggioPhase}
          arpeggioIndex={arpeggioIndex}
          setArpeggioIndex={setArpeggioIndex}
          arpeggioResults={arpeggioResults}
          setArpeggioResults={setArpeggioResults}
          setLastResult={setLastResult}
          engineState={engineState}
          isRecording={isRecording}
          isArpeggioRecording={isArpeggioRecording}
          handleListenChord={handleListenChord}
          handleArpeggioNote={handleArpeggioNote}
          handleArpeggioChord={handleArpeggioChord}
          lastResult={lastResult}
          notes={NOTES}
        />
      )}

      {voicings.length === 0 && (
        <EmptyState
          title="No se encontraron voicings"
          description="Prueba relajar algunos filtros para ver mas opciones."
          filters={[
            ...(filter.voicingType !== "all"
              ? [{
                  key: "voicingType",
                  label: "Tipo de Voicing",
                  count: 1,
                  onClear: () => setFilter((f) => ({ ...f, voicingType: "all" })),
                }]
              : []),
            ...(filter.inversion !== "fundamental"
              ? [{
                  key: "inversion",
                  label: "Inversión",
                  count: 2,
                  onClear: () => setFilter((f) => ({ ...f, inversion: "fundamental" })),
                }]
              : []),
            ...(filter.selectedStrings.length < 4
              ? [{
                  key: "strings",
                  label: "Cuerdas activas",
                  count: 3,
                  onClear: () => setFilter((f) => ({ ...f, selectedStrings: [6, 5, 4, 3, 2, 1] })),
                }]
              : []),
            ...(filter.maxFret < 12
              ? [{
                  key: "maxFret",
                  label: "Traste máximo",
                  count: 4,
                  onClear: () => setFilter((f) => ({ ...f, maxFret: 15 })),
                }]
              : []),
          ]}
        />
      )}
        </>
      )}
    </div>
  );

  // ������ Bottom: navegación + muteo + acciones ������
  const horizontalDock = (
    <ChordHorizontalDock
      activeTab={activeTab as "acorde" | "progresiónes"}
      onTabChange={(t) => setActiveTab(t)}
      root={root}
      qualityLabel={def?.label || quality}
      notes={NOTES}
      onPrevNote={handlePrevNote}
      onNextNote={handleNextNote}
      onSelectNote={setRoot}
      onPrevQuality={handlePrevQuality}
      onNextQuality={handleNextQuality}
      activeCategory={activeChordCategory}
      onCategoryChange={(id) => {
        setActiveChordCategory(id);
        const qs = getQualitiesByCategory(id);
        if (qs.length && !qs.includes(quality)) setQuality(qs[0]);
      }}
      quality={quality}
      onQualityChange={(q) => {
        setQuality(q);
        const cat = CHORD_CATALOG.find((c) => c.quality === q)?.category;
        if (cat) setActiveChordCategory(cat);
      }}
      voicingType={filter.voicingType}
      onVoicingType={(t) => setFilter((f) => ({ ...f, voicingType: t }))}
      inversion={filter.inversion}
      inversionOptions={inversionOptions}
      onPrevInversion={handlePrevInversion}
      onNextInversion={handleNextInversion}
      onPlayChord={
        def ? (surface === "keyboard" ? handlePlayChordPiano : handlePlayChord) : undefined
      }
      onCreateTask={() => setIsTaskModalOpen(true)}
      detectionScore={
        lastResult && !isRecording && !isArpeggioRecording ? lastResult.score : null
      }
      isDetecting={isRecording || isArpeggioRecording || engineState.isLoading}
    />
  );


  return (
    <>
      <TaskGeneratorModal
        isOpen={isTaskModalOpen}
        onClose={() => setIsTaskModalOpen(false)}
        teacherId={teacherId}
        teacherName={teacherName}
        type="chord"
        title={`Acorde ${root} ${def?.label || quality}`}
        description={`Practica el acorde ${root} ${def?.label || quality} en posición ${mode === "classic" ? "clásica" : "explorador"}`}
        config={serializeChordState(root, quality, mode, selectedVoicingIndex, detectionMode)}
        classrooms={classrooms}
        classroomMembers={members}
      />
      <LearnWorkbenchShell
        hideLeftSidebar={isSandbox}
        immersive={showImmersiveChord}
        sidebar={showImmersiveChord ? undefined : sidebar}
        inspector={showImmersiveChord ? undefined : inspector}
        bottom={showImmersiveChord ? undefined : horizontalDock}
        bottomDock={!showImmersiveChord}
        title={
          showImmersiveChord
            ? undefined
            : activeTab === "progresiónes"
            ? `Progresión en ${progKey}`
            : `${root} ${def?.label || quality}`
        }
        activeModule="chords"
        className="min-h-[100dvh] h-[100dvh] overflow-hidden"
      >
      {showImmersiveChord && def ? (
        <ChordImmersiveCanvas
          root={root}
          quality={quality}
          def={def}
          inversion={filter.inversion}
          voicingType={filter.voicingType}
          mode={mode}
          currentVoicing={currentVoicing}
          voicings={voicings}
          selectedVoicingIndex={selectedVoicingIndex}
          onSelectVoicing={setSelectedVoicingIndex}
          mutedStrings={effectiveMutedStrings}
          stringOrder={stringOrder}
          leftHanded={leftHanded}
          onStringOrderToggle={() =>
            setStringOrder(stringOrder === "physical" ? "tab" : "physical")
          }
          onLeftHandedToggle={() => setLeftHanded(!leftHanded)}
          onNextNote={handleNextNote}
          onNextQuality={handleNextQuality}
          onNextInversion={handleNextInversion}
          onCycleVoicingType={handleCycleVoicingType}
          onFretClick={(s, f) => playFret(s, f, 700)}
          onPlayChord={handlePlayChord}
        />
      ) : (
      <div className="flex-1 flex flex-col min-h-0 min-w-0 w-full overflow-hidden p-2">
        {activeTab === "progresiónes" ? (
          <div className="flex-1 flex flex-col w-full min-h-0 gap-2">
            {/* Secuenciador arriba */}
            <div className="shrink-0 w-full">
              <ProgressionSequencer
                progression={progression}
                keySignature={progKey}
                onChange={setProgression}
                activeIndex={progActiveIndex}
              />
            </div>

            <div className="flex-1 min-h-0 w-full">
              {(() => {
                const active = progression[progActiveIndex];
                const parsed = active ? parseChordName(active) : null;
                if (!parsed) {
                  return (
                    <div className="flex items-center justify-center h-full text-slate-400 font-sans text-sm">
                      Selecciona un acorde en la progresión
                    </div>
                  );
                }
                return surface === "keyboard" ? (
                  <ChordPianoWorkbench
                    root={parsed.root}
                    quality={parsed.quality}
                    activeCategory={activeChordCategory}
                    inversion={filter.inversion}
                    voicingType={filter.voicingType}
                    onCategoryChange={setActiveChordCategory}
                    onQualityChange={(q) => setQuality(q)}
                    onKeyClick={(midi) => {
                      const { note, octave } = midiToNoteName(midi);
                      playNote(note, octave, 700);
                    }}
                  />
                ) : (
                  <ChordGuitarWorkbench
                    root={parsed.root}
                    quality={parsed.quality}
                    inversion={filter.inversion}
                    voicingType={filter.voicingType}
                    currentVoicing={progVoicing}
                    voicingIndex={0}
                    voicingCount={progVoicing ? 1 : 0}
                    onPrevVoicing={() => {}}
                    onNextVoicing={() => {}}
                    mutedStrings={progVoicing?.mutedStrings ?? []}
                    stringOrder={stringOrder}
                    leftHanded={leftHanded}
                    neckFade={neckFade}
                    onFretClick={(s, f) => playFret(s, f, 700)}
                  />
                );
              })()}
            </div>

            {/* Player abajo */}
            <div className="shrink-0 w-full max-w-2xl pb-2">
              <ProgressionPlayer
                progression={progression}
                keySignature={progKey}
                bpm={progBpm}
                onBpmChange={setProgBpm}
                isPlaying={progIsPlaying}
                onTogglePlay={setProgIsPlaying}
                activeIndex={progActiveIndex}
                onActiveIndexChange={setProgActiveIndex}
              />
            </div>
          </div>
        ) : surface === "keyboard" ? (
          <ChordPianoWorkbench
            root={root}
            quality={quality}
            activeCategory={activeChordCategory}
            inversion={filter.inversion}
            voicingType={filter.voicingType}
            onCategoryChange={(id) => {
              setActiveChordCategory(id);
              const qs = getQualitiesByCategory(id);
              if (qs.length && !qs.includes(quality)) setQuality(qs[0]);
            }}
            onQualityChange={(q) => {
              setQuality(q);
              const cat = CHORD_CATALOG.find((c) => c.quality === q)?.category;
              if (cat) setActiveChordCategory(cat);
            }}
            onKeyClick={(midi) => {
              const { note, octave } = midiToNoteName(midi);
              playNote(note, octave, 700);
            }}
          />
        ) : (
          <ChordGuitarWorkbench
            root={root}
            quality={quality}
            inversion={filter.inversion}
            voicingType={filter.voicingType}
            currentVoicing={currentVoicing}
            voicingIndex={selectedVoicingIndex}
            voicingCount={voicings.length}
            onPrevVoicing={handlePrev}
            onNextVoicing={handleNext}
            mutedStrings={effectiveMutedStrings}
            stringOrder={stringOrder}
            leftHanded={leftHanded}
            onFretClick={(s, f) => playFret(s, f, 700)}
          />
        )}

      </div>
      )}
    </LearnWorkbenchShell>
    </>
  );
};

export default ChordBuilderPage;
